import React, { useState, useEffect } from 'react';
import { Card, Radio, Button, Space, Typography, message, Progress, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined } from '@ant-design/icons';
import { getQuestions, submitQuiz } from '../../api/learn';

const { Title, Text, Paragraph } = Typography;

const Step4Quiz = ({ onComplete }) => {
  const [questions, setQuestions] = useState([]);
  const [currentQuestion, setCurrentQuestion] = useState(0);
  const [answers, setAnswers] = useState({});
  const [submitted, setSubmitted] = useState(false);
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadQuestions();
  }, []);

  const loadQuestions = async () => {
    try {
      const response = await getQuestions();
      if (response.code === 200) {
        setQuestions(response.data.questions);
      }
    } catch (error) {
      message.error('加载题目失败');
    }
  };

  const handleAnswerChange = (e) => {
    const newAnswers = { ...answers };
    newAnswers[questions[currentQuestion].id] = e.target.value;
    setAnswers(newAnswers);
  };

  const handleSubmit = async () => {
    if (Object.keys(answers).length !== questions.length) {
      message.warning('请回答所有题目');
      return;
    }

    try {
      setLoading(true);
      
      const answersArray = Object.entries(answers).map(([questionId, selectedOption]) => ({
        question_id: parseInt(questionId),
        selected_option: selectedOption
      }));

      const response = await submitQuiz(answersArray);
      
      if (response.code === 200) {
        setSubmitted(true);
        setResult({
          correctCount: response.data.correct_count,
          totalCount: response.data.total_count,
          allCorrect: response.data.correct_count === response.data.total_count
        });
        
        if (response.data.correct_count === response.data.total_count) {
          message.success(`恭喜!全部答对!`);
          setTimeout(() => {
            onComplete();
          }, 2000);
        } else {
          message.warning(`答对${response.data.correct_count}题,共${response.data.total_count}题。需要全部答对才能完成本步骤。`);
        }
      }
    } catch (error) {
      message.error(error.message || '提交失败');
    } finally {
      setLoading(false);
    }
  };

  if (questions.length === 0) {
    return <div style={{ textAlign: 'center', padding: '50px' }}>加载中...</div>;
  }

  const question = questions[currentQuestion];
  const progressPercent = Math.round(((currentQuestion + 1) / questions.length) * 100);
  const answeredCount = Object.keys(answers).length;

  return (
    <div className="step-quiz">
      <div className="step-header">
        <Title level={3}>步骤四:找茬游戏</Title>
        <Text type="secondary">每道题只有一个错误选项,请找出不符合行为准则的做法</Text>
      </div>

      <Progress 
        percent={progressPercent} 
        status="active"
        format={() => `${currentQuestion + 1}/${questions.length}`}
        style={{ marginBottom: 24 }}
      />

      {!submitted ? (
        <>
          <Card 
            className="question-card" 
            title={
              <Space>
                <Text strong style={{ fontSize: 16 }}>题目 {currentQuestion + 1}</Text>
                <Text type="secondary">|</Text>
                <Text type="secondary">准则 {question.item_number}</Text>
              </Space>
            }
          >
            <Space direction="vertical" style={{ width: '100%' }} size="large">
              <Paragraph style={{ fontSize: 16, lineHeight: 1.8, fontWeight: 500 }}>
                {question.question_text}
              </Paragraph>

              <Alert
                message="答题说明"
                description="请在四个选项中找出唯一一个错误的表述或做法"
                type="info"
                showIcon
                style={{ marginBottom: 16 }}
              />

              <Radio.Group 
                onChange={handleAnswerChange} 
                value={answers[question.id]}
                style={{ width: '100%' }}
              >
                <Space direction="vertical" style={{ width: '100%' }} size="middle">
                  <Radio 
                    value="A" 
                    style={{ fontSize: 15, lineHeight: 1.6 }}
                  >
                    A. {question.option_a}
                  </Radio>
                  <Radio 
                    value="B" 
                    style={{ fontSize: 15, lineHeight: 1.6 }}
                  >
                    B. {question.option_b}
                  </Radio>
                  <Radio 
                    value="C" 
                    style={{ fontSize: 15, lineHeight: 1.6 }}
                  >
                    C. {question.option_c}
                  </Radio>
                  <Radio 
                    value="D" 
                    style={{ fontSize: 15, lineHeight: 1.6 }}
                  >
                    D. {question.option_d}
                  </Radio>
                </Space>
              </Radio.Group>
            </Space>
          </Card>

          <div style={{ marginTop: 24, textAlign: 'center' }}>
            <Space size="large">
              <Button
                disabled={currentQuestion === 0}
                onClick={() => setCurrentQuestion(currentQuestion - 1)}
              >
                上一题
              </Button>
              
              <Text>
                已答: {answeredCount}/{questions.length}
              </Text>
              
              {currentQuestion < questions.length - 1 ? (
                <Button
                  type="primary"
                  onClick={() => setCurrentQuestion(currentQuestion + 1)}
                >
                  下一题
                </Button>
              ) : (
                <Button
                  type="primary"
                  onClick={handleSubmit}
                  loading={loading}
                  disabled={answeredCount !== questions.length}
                >
                  提交答案
                </Button>
              )}
            </Space>
          </div>

          {/* 题目导航 */}
          <Card size="small" style={{ marginTop: 24 }} title="题目导航">
            <Space wrap>
              {questions.map((q, index) => (
                <Button
                  key={q.id}
                  size="small"
                  type={currentQuestion === index ? 'primary' : 'default'}
                  onClick={() => setCurrentQuestion(index)}
                  style={{ 
                    minWidth: 40,
                    borderColor: answers[q.id] ? '#52c41a' : undefined
                  }}
                >
                  {index + 1}
                </Button>
              ))}
            </Space>
          </Card>
        </>
      ) : (
        <Card>
          <Space direction="vertical" style={{ width: '100%', textAlign: 'center' }} size="large">
            <div style={{ fontSize: 48 }}>
              {result.allCorrect ? (
                <CheckCircleOutlined style={{ color: '#52c41a' }} />
              ) : (
                <CloseCircleOutlined style={{ color: '#ff4d4f' }} />
              )}
            </div>
            
            <Title level={3}>
              {result.allCorrect ? '恭喜全部答对!' : '答题完成'}
            </Title>
            
            <Paragraph style={{ fontSize: 18 }}>
              答对 <Text strong style={{ fontSize: 24, color: result.allCorrect ? '#52c41a' : '#ff4d4f' }}>{result.correctCount}</Text> 题,
              共 <Text strong>{result.totalCount}</Text> 题
            </Paragraph>

            {result.allCorrect ? (
              <Alert
                message="恭喜完成找茬游戏!"
                description="即将返回学习选择页面..."
                type="success"
                showIcon
              />
            ) : (
              <>
                <Alert
                  message="需要全部答对才能完成本步骤"
                  description="你可以重新挑战,或先学习其他步骤稍后再来"
                  type="warning"
                  showIcon
                />
                <Space size="large">
                  <Button 
                    type="primary" 
                    size="large"
                    onClick={() => {
                      setSubmitted(false);
                      setAnswers({});
                      setCurrentQuestion(0);
                      setResult(null);
                    }}
                  >
                    重新挑战
                  </Button>
                  <Button 
                    size="large"
                    onClick={onComplete}
                  >
                    稍后再来
                  </Button>
                </Space>
              </>
            )}
          </Space>
        </Card>
      )}
    </div>
  );
};

export default Step4Quiz;
